<template>
  <Modal ref="modalRef" width="40rem" @before-show="prepare">
    <template #body>
      <template v-if="!isConfirmed">
        <View gap="xl" direction="column">
          <View gap="md" direction="column" align="center">
            <Text variant="h2">Mark as rented out?</Text>
            <Text variant="body-large" color="secondary" align="center">
              We will consider this property as rented out and stop all ongoing conversations and showings
            </Text>
          </View>

          <Hint gap="md">
            <template #icon>
              <IconTile icon="lightbulb-02" size="small" color="yellow" />
            </template>

            <View gap="sm" direction="column">
              <Text variant="h5">Important notice</Text>
              <Text variant="body-medium">
                Once confirmed, we'll automatically notify all interested renters that this unit is no longer available.
                Your current renter pipeline will close completely, which can be difficult to restore if changed later.
              </Text>
            </View>
          </Hint>

          <WhatToExpectCard :items="whatToExpectNextItems" />
        </View>
      </template>

      <template v-else>
        <View direction="column" gap="lg" align="center" padding-block="lg">
          <Text variant="h1">🎉</Text>
          <Text variant="h2">Congratulations!</Text>
          <Text variant="body-large" color="secondary" align="center">
            We are thrilled to hear that your home has found a new tenant
          </Text>
        </View>
      </template>
    </template>

    <template #footer>
      <View v-if="!isConfirmed" gap="md" :grow="1" class="actions">
        <Button variant="secondary" @click="close">Cancel</Button>
        <Button variant="primary" :loading="isLoading" @click="submit">Mark as rented out</Button>
      </View>

      <Button v-else variant="primary" @click="close">Done</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { useQueryCache } from '@pinia/colada';
import { computed, ref } from 'vue';

import { Button, Hint, IconTile, Modal, Text, View, WhatToExpectCard, WhatToExpectItem } from '@tallo/design-system';
import { applyLinkService } from '@tallo/investor/applications';
import {
  getCompanyWantsToApplyQueryKey,
  getPropertyInquiriesQueryKey,
  usePropertyStore,
} from '@tallo/investor/property';
import { showingsQueryKey } from '@tallo/investor/showing';

const queryCache = useQueryCache();
const propertyStore = usePropertyStore();
const modalRef = ref<InstanceType<typeof Modal>>();
const isLoading = ref(false);
const isConfirmed = ref(false);
const hasApplyLinks = ref(false);
const baseItems: WhatToExpectItem[] = [
  {
    icon: 'x-close-circle',
    description: 'Property will be removed from the listing platforms',
  },
  {
    icon: 'message-chat-circle',
    description: 'All conversations and both pending and confirmed showings will be canceled',
  },
  {
    icon: 'check-circle',
    description:
      'Property will be marked as rented out. New leads will be informed that the unit is no longer available',
  },
];

// Marking it rented out turns the unit's apply links off; said only when it has some
const whatToExpectNextItems = computed<WhatToExpectItem[]>(() =>
  hasApplyLinks.value
    ? [
        ...baseItems,
        {
          icon: 'link-01',
          description:
            'Its apply links will be turned off. You can get a new one any time for another application, without relisting',
        },
      ]
    : baseItems,
);

async function prepare() {
  isConfirmed.value = false;
  hasApplyLinks.value = false;

  const propertyId = propertyStore.property?.id;

  if (!propertyId) return;

  try {
    hasApplyLinks.value = (await applyLinkService.list(propertyId)).length > 0;
  } catch {
    // Only the notice depends on it; marking the unit rented out does not
  }
}

async function submit() {
  isLoading.value = true;

  const propertyId = propertyStore.property?.id;

  propertyStore
    .markAsRentedOut(propertyId!)
    .then(async () => {
      await Promise.all([
        queryCache.invalidateQueries({ key: [showingsQueryKey] }),
        queryCache.invalidateQueries({ key: getPropertyInquiriesQueryKey(propertyId!) }),
        queryCache.invalidateQueries({ key: getCompanyWantsToApplyQueryKey() }),
      ]);
      isConfirmed.value = true;
    })
    .catch((error) => {
      console.error('Error marking property as rented out:', error);
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function close() {
  modalRef.value?.close();
}

defineExpose({
  modalRef,
});
</script>

<style scoped lang="scss">
.actions > * {
  width: 100%;
}
</style>
